import React from 'react';
import useAPI from "../utils/api";
import './NewSiteDialog.scss';
import { timezones, detectTimezoneId } from "../utils/timezones";
import Button from "./Button";
import DialogContent from "@mui/material/DialogContent/DialogContent";
import Dialog from "@mui/material/Dialog/Dialog";
import Slide from "@mui/material/Slide/Slide";
import { TextField, FormControl, MenuItem, Select, DialogActions, InputLabel } from "@mui/material";
import { useSnackbar } from "notistack";
import { useRoleCheck } from '../utils/useRoleCheck';
import { extractPlanTier } from '../components/Editor/utils';
import PaywallLock from '../components/PaywallLock';
import { useUser } from '../utils/contexts';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

function NewSiteDialog({ isOpen }) {
  const API = useAPI();
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const handleRoleCheck = useRoleCheck(API.currentRole);
  const [loading, setLoading] = React.useState(false);
  const [urlError, setUrlError] = React.useState('');
  const [newSite, setNewSite] = React.useState({
    url: "",
    timezone: detectTimezoneId()
  });

  const [open, setOpen] = React.useState(isOpen || false);

  // extractPlanTier maps a site with no plan to 'FREE' (tier 0) and Free Forever
  // to 'FFR' (tier 1) — same helper/thresholding used to gate contact list features
  // (see ContactListDialog.js). Any paid plan is tier 2+.
  // Checked across every site the user OWNS (not just the one currently being
  // viewed). A user who already pays for at least one site is a real customer —
  // an old/bonus free-tier site elsewhere shouldn't block them from adding more.
  const ownedSites = (user?.sites || []).filter((site) => site.current_role === 'owner');
  const hasPaidSite = ownedSites.some((site) => extractPlanTier(site.plan) >= 2);
  const hasFreeTierSite = ownedSites.some((site) => extractPlanTier(site.plan) < 2);
  const isNewSiteLocked = !hasPaidSite && hasFreeTierSite;

  const createSite = async (form) => {
    setLoading(true);
    const [ok, response] = await API.createSite(form);
    if (ok) {
      enqueueSnackbar(`Site "${form.url}" has been added successfully!`, { variant: 'success' });
      sessionStorage.setItem('session_signed_up', 'true');
      window.location.pathname = `/sites/${response.id}/`;
      setOpen(false);
    } else if (response?.field === 'url') {
      // The API's banned-domain message is playful; the dialog shows a plain one instead.
      setUrlError(
        response.message?.startsWith('Enter a valid website address')
          ? response.message
          : "This website address can't be used. Please enter your own website's address."
      );
    } else {
      enqueueSnackbar(response.message, { variant: 'error' });
    }
    setLoading(false);
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    await createSite(newSite);
  }

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    if (name === 'url') { setUrlError(''); }
    setNewSite(data => ({
      ...data,
      [name]: value
    }));
  }

  const handleClickOpen = (event) => {
    // To check allowed role to perform action
    if (handleRoleCheck()) { return; }
    if (isNewSiteLocked) { return; }
    event.preventDefault();
    setOpen(true);
  };

  const handleClose = (event) => {
    event.preventDefault();
    setOpen(false);
  };

  return (
    <React.Fragment>
      <div className="button-wrapper" style={{ position: 'relative' }}>
        {isNewSiteLocked && (
          <PaywallLock
            message="Not available on your plan. Please upgrade your subscription."
            url={API.siteId ? `/sites/${API.siteId}/upgrade` : ''}
          />
        )}
        <Button onClick={handleClickOpen} color="white" variant="text" disabled={isNewSiteLocked}>Add New Site</Button>
      </div>
      <Dialog
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth={true}
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <h4 style={{marginBottom: '10px'}}>Create A New Site</h4>
          <form id="siteForm" onSubmit={handleSubmit} validate="validate">
            <FormControl fullWidth>
              <TextField required type="url" pattern="https?://.*" value={newSite.url ? newSite.url : 'https://'} onChange={handleInputChange} name="url" autoFocus
                         error={!!urlError} helperText={urlError}
                         label="URL (http://)" size='small'/>
            </FormControl>
            <FormControl fullWidth style={{ marginTop: '20px' }}>
              <InputLabel>Timezone</InputLabel>
              <Select required value={newSite.timezone} onChange={handleInputChange} name="timezone" size='small'>
                {timezones.map(({ title, id }) => <MenuItem key={id} value={id}>{title}</MenuItem>)}
              </Select>
            </FormControl>
          </form>
        </DialogContent>
        <DialogActions>
          <Button type="submit" loading={loading} color="primary" form="siteForm">Create Site</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

export default NewSiteDialog;
